import { AlertTriangle, RotateCcw } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import { useNegotiationHistoryV2Query } from '../../hooks/use-negotiation-history'
import type { NegotiationHistoryEntry } from '../../services/negotiations-history-v2.service'

/**
 * El historial del panel de detalle — ya contra API v2.
 *
 * ERA EL CONSUMIDOR QUE FALTABA. El historial tiene DOS superficies —el paso «Historial de
 * estados» del expediente y este tab del panel lateral— y sólo la primera se había migrado.
 * Mientras quedara ésta, `GET /api/v1/negotiations/:id/history-states` seguía teniendo
 * consumidor, y la regla de cierre del proyecto es explícita: una capacidad está migrada
 * cuando lo están TODOS sus consumidores, no cuando existe el endpoint v2.
 *
 * Pinta lo mismo que antes con los nombres del contrato v2: `state` por `nextState`,
 * `workflow` por `nextWorkflow`, `at`/`until` por `startDate`/`endDate` y `by` por
 * `updatedBy`. Un `until` nulo sigue queriendo decir «Vigente».
 *
 * **El comentario NO se corre a la fila siguiente**, y acá nunca se corría: v1 guardaba la
 * TRANSICIÓN, así que la pantalla del expediente leía el comentario de la entrada siguiente
 * para responder «por qué salió de este estado». El contrato v2 publica cada comentario
 * pegado a la transición que lo produjo, con lo que el corrimiento dejó de hacer falta y las
 * dos superficies quedan diciendo lo mismo.
 */
function formatDateTime(value?: string | null): string {
  if (!value) return 'Vigente'
  return formatHumanDateTime(value, 'Fecha no disponible')
}

function formatDuration(start?: string | null, end?: string | null): string | null {
  if (!start) return null
  const startDate = new Date(start)
  const endDate = end ? new Date(end) : new Date()
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null

  const totalMinutes = Math.max(0, Math.floor((endDate.getTime() - startDate.getTime()) / 60_000))
  const days = Math.floor(totalMinutes / 1_440)
  const hours = Math.floor((totalMinutes % 1_440) / 60)
  if (days > 0) return `${days} d ${hours} h`
  if (hours > 0) return `${hours} h`
  return `${totalMinutes} min`
}

function HistoryEntry({ entry, isLast }: { entry: NegotiationHistoryEntry; isLast: boolean }) {
  const stateName = entry.state.name
  const duration = formatDuration(entry.at, entry.until)

  return (
    <li className="relative pl-7">
      {!isLast ? (
        <span
          aria-hidden="true"
          className="absolute left-[7px] top-4 h-[calc(100%+0.5rem)] w-px bg-border"
        />
      ) : null}
      <span
        aria-hidden="true"
        className="absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full border-2 border-background ring-1 ring-border"
        style={{ backgroundColor: entry.state.color ?? 'hsl(var(--muted-foreground))' }}
      />

      <div className="rounded-lg border p-3">
        <div className="flex min-w-0 items-start justify-between gap-2">
          <div className="min-w-0">
            <h4 className="truncate text-sm font-semibold" title={stateName}>
              {stateName}
            </h4>
            {entry.workflow?.name ? (
              <p className="truncate text-xs text-muted-foreground">{entry.workflow.name}</p>
            ) : null}
          </div>
          {entry.isRejected ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-destructive/20 bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive">
              <AlertTriangle aria-hidden="true" className="h-3 w-3" />
              Rechazada
            </span>
          ) : null}
        </div>

        <p className="mt-2 text-xs text-muted-foreground">
          {formatDateTime(entry.at)} — {formatDateTime(entry.until)}
          {duration ? ` · ${duration}` : ''}
        </p>

        {entry.comment ? (
          <p className="mt-2 break-words rounded-md bg-muted/60 px-2 py-1.5 text-xs">
            {entry.comment}
          </p>
        ) : null}

        {entry.by?.fullname ? (
          <p className="mt-2 truncate text-[11px] text-muted-foreground">
            Procesado por {entry.by.fullname}
          </p>
        ) : null}
      </div>
    </li>
  )
}

/**
 * Recibe el `neg_` y no el `_id`: el pipe de v2 rechaza el id legacy.
 *
 * Es NULLABLE porque el schema lo declara opcional — las negociaciones anteriores al backfill
 * pueden no tenerlo. Medido en `idc-real-estate-dev`: los 172 documentos lo tienen, así que el
 * caso es raro, no imposible.
 */
export function NegotiationHistoryTab({ publicId }: { publicId: string | null }) {
  const { data: history = [], isLoading, isError, refetch } = useNegotiationHistoryV2Query(publicId)

  // SIN `neg_` NO SE PREGUNTÓ, y eso no es lo mismo que «no hay historial».
  //
  // La query queda deshabilitada, así que sin este corte se caería en el vacío de abajo y la
  // pantalla afirmaría que la negociación nunca cambió de estado. Es la misma clase de mentira
  // que tuvo muerto este endpoint durante meses: un resultado vacío que parece un dato.
  if (!publicId) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-center">
        <p className="text-sm font-medium">Historial no disponible</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Esta negociación no tiene identificador público, que es lo que necesita la lectura del
          historial.
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-24 w-full" />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-center">
        <p className="text-sm font-medium">No se pudo cargar el historial</p>
        <p className="mt-1 text-xs text-muted-foreground">Intentá nuevamente.</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => {
            void refetch()
          }}
        >
          <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
          Reintentar
        </Button>
      </div>
    )
  }

  if (history.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-center">
        <p className="text-sm font-medium">Sin movimientos registrados</p>
        <p className="mt-1 text-xs text-muted-foreground">
          El historial aparecerá cuando la negociación cambie de estado.
        </p>
      </div>
    )
  }

  const reversedHistory = [...history].reverse()

  return (
    <ol className="flex flex-col gap-2">
      {reversedHistory.map((entry, index) => (
        <HistoryEntry
          key={`${entry.state.id}-${entry.at ?? index}`}
          entry={entry}
          isLast={index === reversedHistory.length - 1}
        />
      ))}
    </ol>
  )
}
